/* Quiz attempt submission and its offline queue.
 *
 * Quiz attempts do NOT ride the session-event outbox: each client record family
 * travels on its own endpoint (AP2), so this is a separate durable queue with
 * the same idempotency discipline — a client-minted `attempt_id` means a retry
 * after a lost response is deduped rather than double-counted.
 *
 * Offline, the learner sees the provisional LOCAL score. The server re-grades at
 * sync and its value wins (A14); the UI reconciles silently.
 */

import { api, ApiFailure, type QuizAttemptResult } from './api';
import { getMeta, setMeta } from './db';
import type { QuizAnswer } from './screens/quiz';

const META_PENDING = 'pending_quiz_attempts';

export interface PendingAttempt {
  attempt_id: string;
  unit_id: string;
  unit_version: number;
  started_at: string;
  submitted_at: string;
  answers: {
    item_id: string;
    answer?: unknown;
    confidence?: number | undefined;
    skipped?: boolean | undefined;
  }[];
  client_score: number;
}

function uuidV4(): string {
  const c: Crypto = crypto;
  if (typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const hex = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function queue(attempt: PendingAttempt): Promise<void> {
  const pending = (await getMeta<PendingAttempt[]>(META_PENDING)) ?? [];
  await setMeta(META_PENDING, [...pending, attempt]);
}

export async function pendingAttemptCount(): Promise<number> {
  return ((await getMeta<PendingAttempt[]>(META_PENDING)) ?? []).length;
}

/**
 * Build the attempt, try to send it, and durably queue it if that fails.
 * Returns the SERVER result when available, otherwise null (provisional).
 */
export async function submitQuizAttempt(
  unitId: string,
  unitVersion: number,
  answers: QuizAnswer[],
  startedAt: string,
): Promise<QuizAttemptResult | null> {
  const attempt: PendingAttempt = {
    attempt_id: uuidV4(),
    unit_id: unitId,
    unit_version: unitVersion,
    started_at: startedAt,
    submitted_at: new Date().toISOString(),
    /* A skipped item carries NO answer and NO confidence (02 §6.10, AP17): the
     * server scores it incorrect and excludes it from calibration. Inventing a
     * "Guess" here would quietly poison the calibration curve with a value the
     * learner never claimed. */
    answers: answers.map((a) =>
      a.skipped
        ? { item_id: a.item_id, skipped: true }
        : { item_id: a.item_id, answer: a.answer, confidence: a.confidence ?? undefined },
    ),
    // Recorded for drift diagnostics only; the server discards it as truth.
    client_score: answers.length
      ? answers.filter((a) => a.correct).length / answers.length
      : 0,
  };

  // Queue FIRST, so a crash between send and acknowledge cannot lose the attempt.
  await queue(attempt);

  try {
    const result = await api.postQuizAttempt(attempt);
    await removePending(attempt.attempt_id);
    return result;
  } catch (err) {
    if (err instanceof ApiFailure && err.code === 'OFFLINE') return null;
    // A 4xx means the server will never accept it; dropping it beats retrying
    // forever. Anything else stays queued for the next drain.
    if (err instanceof ApiFailure && err.status >= 400 && err.status < 500) {
      await removePending(attempt.attempt_id);
    }
    return null;
  }
}

async function removePending(attemptId: string): Promise<void> {
  const pending = (await getMeta<PendingAttempt[]>(META_PENDING)) ?? [];
  await setMeta(META_PENDING, pending.filter((a) => a.attempt_id !== attemptId));
}

/** Drain queued attempts. Safe to call repeatedly — every send is idempotent. */
export async function drainQuizAttempts(): Promise<number> {
  const pending = (await getMeta<PendingAttempt[]>(META_PENDING)) ?? [];
  let sent = 0;

  for (const attempt of pending) {
    try {
      await api.postQuizAttempt(attempt);
      await removePending(attempt.attempt_id);
      sent++;
    } catch (err) {
      if (err instanceof ApiFailure && err.status >= 400 && err.status < 500) {
        await removePending(attempt.attempt_id);
        continue;
      }
      break; // offline or server trouble: retry on the next drain
    }
  }
  return sent;
}
